import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ChevronRight } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
      // Select first category by default
      if (response.data.length > 0) {
        setSelectedCategory(response.data[0]);
        fetchFeaturedForCategory(response.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedForCategory = async (categoryId) => {
    try {
      const response = await axios.get(`${API}/products?category=${categoryId}&featured=true`);
      // Get up to 4 featured products, or if none featured, get first 4 products
      let products = response.data.slice(0, 4);
      if (products.length === 0) {
        const allProducts = await axios.get(`${API}/products?category=${categoryId}`);
        products = allProducts.data.slice(0, 4);
      }
      setFeaturedProducts(products);
    } catch (error) {
      console.error("Error fetching products:", error);
      setFeaturedProducts([]);
    }
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    fetchFeaturedForCategory(category.id);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section */}
      <section className="py-20 md:py-28 border-b border-border/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl">
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-primary">
              Our Collections
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-foreground mt-4 leading-tight">
              Browse All Categories
            </h1>
            <p className="text-foreground/60 mt-6 text-lg leading-relaxed">
              Explore our curated collections of handcrafted home decor, each piece thoughtfully 
              sourced from skilled artisans across India.
            </p>
          </div>
        </div>
      </section>

      {/* Categories Grid with Preview */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-12">
            
            {/* Left - Category List */}
            <div className="lg:col-span-4">
              <h2 className="text-xs font-medium uppercase tracking-[0.2em] text-primary mb-8">
                Select a Category
              </h2>
              <div className="space-y-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategorySelect(category)}
                    className={`w-full text-left p-4 rounded-lg transition-all duration-300 group flex items-center justify-between ${
                      selectedCategory?.id === category.id
                        ? "bg-primary/10 border border-primary/30"
                        : "bg-card border border-border/50 hover:border-primary/30 hover:bg-muted/50"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                        <img
                          src={category.image}
                          alt={category.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <span className={`font-heading text-lg ${
                        selectedCategory?.id === category.id
                          ? "text-primary"
                          : "text-foreground"
                      }`}>
                        {category.name}
                      </span>
                    </div>
                    <ChevronRight className={`w-5 h-5 transition-transform ${
                      selectedCategory?.id === category.id
                        ? "text-primary translate-x-1"
                        : "text-foreground/30 group-hover:text-primary group-hover:translate-x-1"
                    }`} />
                  </button>
                ))}
              </div>
            </div>

            {/* Right - Featured Products Preview */}
            <div className="lg:col-span-8">
              {selectedCategory && (
                <div className="animate-fade-in">
                  <div className="flex items-end justify-between mb-8">
                    <div>
                      <h2 className="text-xs font-medium uppercase tracking-[0.2em] text-primary">
                        Featured in {selectedCategory.name}
                      </h2>
                      <p className="text-foreground/60 mt-2 text-sm">
                        {selectedCategory.description || `Discover our ${selectedCategory.name.toLowerCase()} collection`}
                      </p>
                    </div>
                    <Link 
                      to={`/category/${selectedCategory.id}`}
                      className="text-primary hover:text-primary/80 font-medium flex items-center gap-2 text-sm"
                    >
                      View All
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>

                  {featuredProducts.length > 0 ? (
                    <div className="grid grid-cols-2 gap-4">
                      {featuredProducts.map((product, index) => (
                        <Link
                          key={product.id}
                          to={`/product/${product.id}`}
                          className="group relative rounded-lg overflow-hidden bg-card border border-border/50 hover:border-primary/30 transition-all duration-300"
                          style={{ animationDelay: `${index * 0.1}s` }}
                        >
                          <div className="aspect-square overflow-hidden">
                            <img
                              src={product.image_url || product.images?.[0]}
                              alt={product.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                          <div className="p-4">
                            <h3 className="font-heading text-foreground text-sm truncate group-hover:text-primary transition-colors">
                              {product.name}
                            </h3>
                            {product.is_featured && (
                              <span className="inline-block mt-2 text-[10px] font-medium uppercase tracking-wider text-primary">
                                Featured
                              </span>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-64 bg-card border border-border/50 rounded-lg">
                      <p className="text-foreground/40">No products in this category yet</p>
                    </div>
                  )}

                  {/* View Category CTA */}
                  <Link
                    to={`/category/${selectedCategory.id}`}
                    className="mt-8 w-full flex items-center justify-center gap-3 p-5 rounded-lg bg-primary/5 border border-primary/20 hover:bg-primary/10 hover:border-primary/40 transition-all group"
                  >
                    <span className="text-primary font-medium">
                      Explore All {selectedCategory.name}
                    </span>
                    <ArrowRight className="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CategoriesPage;
